import { pgTable, text, serial, integer, boolean, jsonb, timestamp } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Users table (matching Supabase schema with additional columns for app functionality)
export const users = pgTable("users", {
  id: serial("id").primaryKey(), // INTEGER with sequence (matches Supabase)
  email: text("email").notNull().unique(),
  password: text("password"),
  name: text("name"),
  profilePicture: text("profile_picture"),
  provider: text("provider").default('email'),
  providerId: text("provider_id"),
  // Stripe customer mapping (nullable, unique at DB level via constraint/index)
  stripeCustomerId: text("stripe_customer_id"),
  emailVerified: boolean("email_verified").default(false),
  resetToken: text("reset_token"),
  resetTokenExpiry: timestamp("reset_token_expiry"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  credits: integer("credits").default(0).notNull(),
  planTier: text("plan_tier").default('free'),
  // New fields for monthly plan credits management
  planCredits: integer("plan_credits").default(0).notNull(),
  nextCreditsResetAt: timestamp("next_credits_reset_at"),
  // Consultation credits management
  consultationCredits: integer("consultation_credits").default(0).notNull(),
  nextConsultationResetAt: timestamp("next_consultation_reset_at"),
  // Referral system columns
  referralCode: text("referral_code").unique(),
  referredBy: text("referred_by"),
  totalReferrals: integer("total_referrals").default(0).notNull(),
  bonusCredits: integer("bonus_credits").default(0).notNull(),
  // Coach system: account type ('player' | 'coach' | 'admin')
  accountType: text("account_type").default('player'),
});

export const userProfiles = pgTable("user_profiles", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id), // Reference to users table
  // Personal Information
  height: integer("height").notNull(), // in cm
  weight: integer("weight").notNull(), // in kg
  age: integer("age").notNull(),
  gender: text("gender").notNull(),
  
  // Jumping Style
  jumpingStyle: text("jumping_style").notNull(), // one_foot, two_feet, both_equally, not_sure
  
  // Training Commitment
  trainingFrequency: text("training_frequency").notNull(), // 4, 5, 6
  
  // Free Weights Equipment
  dumbbells: text("dumbbells").array().default([]), // 1-10kg, 11-20kg, 21-40kg, 41+kg, none
  kettlebells: text("kettlebells").array().default([]), // 1-8kg, 9-16kg, 17-24kg, 25+kg, none
  weightPlates: text("weight_plates").array().default([]), // up_to_10kg, 11-60kg, 61-100kg, over_100kg, none
  barbells: text("barbells").array().default([]), // 1-20kg, 21-40kg, 41-60kg, 61+kg, none
  legPressAccess: text("leg_press_access").array().default([]), // up_to_100kg, 101-150kg, 151-200kg, 201+kg, none
  
  // Weighted Balls & Bags
  medicineBalls: text("medicine_balls").array().default([]), // 1-4kg, 5-8kg, 9-12kg, 13+kg, none
  sandbags: text("sandbags").array().default([]), // up_to_10kg, 11-20kg, 21-30kg, 31+kg, none
  
  // Explosiveness & Agility Tools
  jumpSurfaces: text("jump_surfaces").array().default([]), // plyometric_box, sturdy_chair, stairs, none
  jumpRope: text("jump_rope").array().default([]), // speed_rope, heavy_rope, none
  resistanceBands: boolean("resistance_bands").default(false),
  agilityLadder: text("agility_ladder").notNull(), // yes, no, chalk_tape_ladder
  
  // Loaded Carries & Sleds
  farmersCarry: text("farmers_carry").array().default([]), // 1-20kg, 21-40kg, 41-60kg, 61+kg, none
  sledAccess: text("sled_access").array().default([]), // 1-40kg, 41-80kg, 81-120kg, 121+kg, none
  
  // Mobility & Recovery
  mobilityEquipment: text("mobility_equipment").array().default([]), // yoga_mat, carpeted_floor, foam_roller, towel_blanket, none
  
  // Racks
  rackAccess: text("rack_access").notNull(), // full_power_rack, half_rack, squat_stand, none
  
  // Hoop or Jumping Surface Access
  courtAccess: text("court_access").notNull(), // indoor_gym, outdoor_court, flat_ground_wall
  
  // Injury History
  injuryHistory: text("injury_history").array().default([]), // knee_pain, ankle_sprain, hip_groin_strain, shin_splints, lower_back_pain, hamstring_quad_strain, core_abdominal_injury, shoulder_elbow_wrist_issue, other, no_injuries
  otherInjury: text("other_injury"), // optional text field for "other" injury
  
  // Optional Stats
  standingVertical: integer("standing_vertical"), // optional, in cm
  runningVertical: integer("running_vertical"), // optional, in cm
  armSpan: integer("arm_span"), // optional, in cm
  squatMax: integer("squat_max"), // optional, in kg
  deadliftMax: integer("deadlift_max"), // optional, in kg
  
  createdAt: text("created_at").default('now()'),
});

export const trainingPlans = pgTable("training_plans", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id), // Reference to users table
  name: text("name"), // Custom plan name (optional, defaults to "Training Plan #X")
  planData: jsonb("plan_data").notNull(), // Complete 7-day plan
  userProfile: jsonb("user_profile"), // Store the user profile data used to generate the plan
  createdAt: timestamp("created_at").defaultNow(),
  // Coach system: track team context when plan was created
  teamIdAtCreation: integer("team_id_at_creation"), // null if individual plan
  generatedFor: integer("generated_for").references(() => users.id), // athlete the plan was made for (if coach generated)
});

export const exercises = pgTable("exercises", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  description: text("description").notNull(),
  instructions: text("instructions").array().notNull(),
  formCues: text("form_cues").array().notNull(),
  purpose: text("purpose").notNull(),
  alternatives: text("alternatives").array(),
  difficulty: text("difficulty").notNull(), // beginner, intermediate, advanced
  category: text("category").notNull(), // warmup, strength, plyometric, recovery
});

// Consultations table for paid support submissions
export const consultations = pgTable("consultations", {
  id: text("id").primaryKey(),
  userId: integer("user_id").references(() => users.id).notNull(),
  subject: text("subject").notNull(),
  message: text("message").notNull(),
  attachments: jsonb("attachments").default([]),
  status: text("status").default('pending'),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  respondedAt: timestamp("responded_at"),
  // Track which team's credits were used (null if personal credits)
  teamIdAtCreation: integer("team_id_at_creation").references(() => teams.id),
});

// Referral system tracking table  
export const referrals = pgTable("referrals", {
  id: serial("id").primaryKey(),
  referrerCode: text("referrer_code").notNull(), // Who made the referral
  referredUserId: text("referred_user_id").notNull(), // Who was referred
  referredAt: timestamp("referred_at").defaultNow().notNull(),
  bonusCreditsAwarded: integer("bonus_credits_awarded").default(0).notNull(), // Credits given to referrer
});

// Stripe webhook idempotency tracking
export const stripeEvents = pgTable("stripe_events", {
  eventId: text("event_id").primaryKey(),
  type: text("type"),
  processedAt: timestamp("processed_at").defaultNow().notNull(),
  userId: integer("user_id"),
  notes: text("notes"),
});

// Exercise video feedback for AI form analysis
export const exerciseVideoFeedback = pgTable("exercise_video_feedback", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id).notNull(),
  trainingPlanId: integer("training_plan_id").references(() => trainingPlans.id).notNull(),
  dayNumber: integer("day_number").notNull(),
  exerciseName: text("exercise_name").notNull(),
  videoUrl: text("video_url").notNull(),
  videoStoragePath: text("video_storage_path").notNull(), // Path in Supabase storage for URL refresh
  videoMimeType: text("video_mime_type").notNull(),
  videoDurationSeconds: integer("video_duration_seconds"),
  feedbackJson: jsonb("feedback_json").$type<VideoFeedbackAnalysis | null>(), // Gemini analysis result
  status: text("status").default('pending').notNull(), // pending, processing, completed, failed
  errorMessage: text("error_message"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
  // Coach system: track team context when video was uploaded (FK added in migration)
  teamIdAtCreation: integer("team_id_at_creation"),
});

// ============================================
// COACH SYSTEM TABLES
// ============================================

// Teams table for coach-managed teams
export const teams = pgTable("teams", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  coachUserId: integer("coach_user_id").references(() => users.id).notNull(),
  inviteCode: text("invite_code").unique().notNull(), // 8-character unique code
  inviteCodeExpiresAt: timestamp("invite_code_expires_at"), // optional expiration
  teamCredits: integer("team_credits").default(0).notNull(),
  planTeamCredits: integer("plan_team_credits").default(0).notNull(), // Team's credit quota (like planCredits for users)
  nextTeamCreditsResetAt: timestamp("next_team_credits_reset_at"),
  // Team consultation credits (mirrors personal consultation credits)
  teamConsultationCredits: integer("team_consultation_credits").default(0).notNull(),
  planTeamConsultationCredits: integer("plan_team_consultation_credits").default(0).notNull(),
  nextTeamConsultationCreditsResetAt: timestamp("next_team_consultation_credits_reset_at"),
  maxMembers: integer("max_members").default(50).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Team memberships - links players to teams
export const teamMemberships = pgTable("team_memberships", {
  id: serial("id").primaryKey(),
  teamId: integer("team_id").references(() => teams.id).notNull(),
  userId: integer("user_id").references(() => users.id).notNull(),
  joinedAt: timestamp("joined_at").defaultNow().notNull(),
  removedAt: timestamp("removed_at"), // soft delete - null means active
  removedBy: integer("removed_by").references(() => users.id), // who removed (coach or self)
  canUseTeamCredits: boolean("can_use_team_credits").default(true).notNull(),
});

// Team invites - tracks invite code usage
export const teamInvites = pgTable("team_invites", {
  id: serial("id").primaryKey(),
  teamId: integer("team_id").references(() => teams.id).notNull(),
  code: text("code").notNull(),
  createdBy: integer("created_by").references(() => users.id).notNull(),
  usedBy: integer("used_by").references(() => users.id), // null until used
  usedAt: timestamp("used_at"),
  expiresAt: timestamp("expires_at"), // optional expiration
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Coach notes - notes on videos or plans
export const coachNotes = pgTable("coach_notes", {
  id: serial("id").primaryKey(),
  coachUserId: integer("coach_user_id").references(() => users.id).notNull(),
  athleteUserId: integer("athlete_user_id").references(() => users.id).notNull(),
  teamId: integer("team_id").references(() => teams.id).notNull(),
  targetType: text("target_type").notNull(), // 'video' | 'plan'
  targetId: integer("target_id").notNull(), // ID of video feedback or plan
  content: text("content").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Credit transactions - audit log for credit usage
export const creditTransactions = pgTable("credit_transactions", {
  id: serial("id").primaryKey(),
  teamId: integer("team_id").references(() => teams.id), // null for personal credits
  userId: integer("user_id").references(() => users.id).notNull(), // who consumed
  planId: integer("plan_id").references(() => trainingPlans.id), // what was generated
  creditsUsed: integer("credits_used").default(1).notNull(),
  source: text("source").notNull(), // 'team' | 'personal' | 'bonus'
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Schemas
export const insertUserProfileSchema = createInsertSchema(userProfiles).omit({
  id: true,
  createdAt: true,
});

export const insertTrainingPlanSchema = createInsertSchema(trainingPlans).omit({
  id: true,
  createdAt: true,
});

export const insertExerciseSchema = createInsertSchema(exercises).omit({
  id: true,
});

export const insertConsultationSchema = createInsertSchema(consultations).omit({
  id: true,
  createdAt: true,
  respondedAt: true,
});

export const insertReferralSchema = createInsertSchema(referrals).omit({
  id: true,
  referredAt: true,
});

export const insertExerciseVideoFeedbackSchema = createInsertSchema(exerciseVideoFeedback).omit({
  id: true,
  createdAt: true,
  completedAt: true,
});

// Coach system insert schemas
export const insertTeamSchema = createInsertSchema(teams).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertTeamMembershipSchema = createInsertSchema(teamMemberships).omit({
  id: true,
  joinedAt: true,
});

export const insertTeamInviteSchema = createInsertSchema(teamInvites).omit({
  id: true,
  createdAt: true,
});

export const insertCoachNoteSchema = createInsertSchema(coachNotes).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertCreditTransactionSchema = createInsertSchema(creditTransactions).omit({
  id: true,
  createdAt: true,
});

export const insertUserSchema = createInsertSchema(users).omit({
  createdAt: true,
  updatedAt: true,
});

// Authentication schemas
export const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

export const signupSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

export const forgotPasswordSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

export const resetPasswordSchema = z.object({
  token: z.string(),
  password: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

// Types
export type InsertUserProfile = z.infer<typeof insertUserProfileSchema>;
export type UserProfile = typeof userProfiles.$inferSelect;
export type InsertTrainingPlan = z.infer<typeof insertTrainingPlanSchema>;
export type TrainingPlan = typeof trainingPlans.$inferSelect;
export type Exercise = typeof exercises.$inferSelect;
export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;
export type InsertReferral = z.infer<typeof insertReferralSchema>;
export type Referral = typeof referrals.$inferSelect;
export type Consultation = typeof consultations.$inferSelect;
export type InsertConsultation = z.infer<typeof insertConsultationSchema>;
export type ExerciseVideoFeedback = typeof exerciseVideoFeedback.$inferSelect;
export type InsertExerciseVideoFeedback = z.infer<typeof insertExerciseVideoFeedbackSchema>;

// Coach system types
export type Team = typeof teams.$inferSelect;
export type InsertTeam = z.infer<typeof insertTeamSchema>;
export type TeamMembership = typeof teamMemberships.$inferSelect;
export type InsertTeamMembership = z.infer<typeof insertTeamMembershipSchema>;
export type TeamInvite = typeof teamInvites.$inferSelect;
export type InsertTeamInvite = z.infer<typeof insertTeamInviteSchema>;
export type CoachNote = typeof coachNotes.$inferSelect;
export type InsertCoachNote = z.infer<typeof insertCoachNoteSchema>;
export type CreditTransaction = typeof creditTransactions.$inferSelect;
export type InsertCreditTransaction = z.infer<typeof insertCreditTransactionSchema>;

// Video feedback analysis types (from Gemini API response)
export interface VideoFeedbackCategory {
  timestamp: string;
  observation: string;
  fix: string;
}

export interface VideoFeedbackPositiveCategory {
  timestamp: string;
  observation: string;
  maintain: string;
}

export interface VideoFeedbackAnalysis {
  exercise: string;
  analysis: {
    alignment_posture: VideoFeedbackCategory;
    range_of_motion: VideoFeedbackCategory;
    tempo_control: VideoFeedbackCategory;
    biggest_error: VideoFeedbackCategory;
    injury_risk: VideoFeedbackCategory;
    whats_working: VideoFeedbackPositiveCategory;
  };
}

export type LoginData = z.infer<typeof loginSchema>;
export type SignupData = z.infer<typeof signupSchema>;
export type ForgotPasswordData = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordData = z.infer<typeof resetPasswordSchema>;

// Utility functions for referral system
export function generateReferralCode(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

export function calculateReferralBonus(totalReferrals: number): number {
  // Referral reward tiers:
  // 2 referrals → 3 extra credits
  // 5 referrals → 10 extra credits  
  // 8 referrals → 18 extra credits
  
  if (totalReferrals >= 8) return 18;
  if (totalReferrals >= 5) return 10;
  if (totalReferrals >= 2) return 3;
  return 0;
}

export function getNextReferralTier(totalReferrals: number): { needed: number; reward: number } | null {
  if (totalReferrals < 2) return { needed: 2 - totalReferrals, reward: 3 };
  if (totalReferrals < 5) return { needed: 5 - totalReferrals, reward: 10 };
  if (totalReferrals < 8) return { needed: 8 - totalReferrals, reward: 18 };
  return null; // Max tier reached
}

// Coach system utility functions
export function generateTeamInviteCode(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

// Team credit quotas by plan tier
// Mirrors personal credit quotas: free(0), pro_starter(15), elite_pro(40), ultra_team(120)
export const TEAM_CREDIT_QUOTAS: Record<string, number> = {
  free: 3,
  pro_starter: 15,
  elite_pro: 40,
  ultra_team: 120,
};

export function getTeamCreditQuota(planTier: string): number {
  return TEAM_CREDIT_QUOTAS[planTier] ?? TEAM_CREDIT_QUOTAS.free;
}

// Team consultation credit quotas by plan tier
// Mirrors personal consultation quotas: free(0), pro_starter(5), elite_pro(10), ultra_team(30)
export const TEAM_CONSULTATION_CREDIT_QUOTAS: Record<string, number> = {
  free: 0,
  pro_starter: 5,
  elite_pro: 10,
  ultra_team: 30,
};

export function getTeamConsultationCreditQuota(planTier: string): number {
  return TEAM_CONSULTATION_CREDIT_QUOTAS[planTier] ?? TEAM_CONSULTATION_CREDIT_QUOTAS.free;
}
